import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatMessage, CustomField, CSVJobStatus, DebugInfo } from '../types';
import {
  sendChatMessage,
  sendStructuredChatMessage,
  uploadCSVBatch,
  getCSVJobStatus,
  getCSVDownloadUrl
} from '../services/api';
import { formatCompleteResponse, copyToClipboard } from '../utils/formatCompleteResponse';
import './ComplianceChatbot.css';

interface ComplianceChatbotProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName?: string;
}

const ComplianceChatbot: React.FC<ComplianceChatbotProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      type: 'system',
      content: `Welcome to Compliance Bot! Ask questions about ${projectName || 'your project'}. Use "Fields" to add custom response fields, or "CSV Batch" for bulk processing.`,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showFieldsPanel, setShowFieldsPanel] = useState(false);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [showCSVModal, setShowCSVModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessingCSV, setIsProcessingCSV] = useState(false);
  const [expandedDebug, setExpandedDebug] = useState<Set<string>>(new Set()); // Track which messages have debug expanded
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null); // Track which message was just copied

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle copy to clipboard for bot messages
  const handleCopyResponse = async (msg: ChatMessage) => {
    const completeText = formatCompleteResponse(
      msg.content,
      msg.sources || []
    );

    const success = await copyToClipboard(completeText);
    if (success) {
      setCopiedMsgId(msg.id);
      setTimeout(() => setCopiedMsgId(null), 2000); // Reset after 2 seconds
    }
  };

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'>) => {
    const newMessage: ChatMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
    return newMessage.id;
  }, []);

  const handleSendMessage = async () => {
    const message = inputValue.trim();
    if (!message || isLoading) return;

    const hasCustomFields = customFields.length > 0;
    let userContent = message;
    if (hasCustomFields) {
      userContent += ` [+ ${customFields.length} custom field(s)]`;
    }

    addMessage({ type: 'user', content: userContent });
    setInputValue('');
    setIsLoading(true);

    try {
      if (hasCustomFields) {
        const response = await sendStructuredChatMessage(message, projectId, customFields);
        addMessage({
          type: 'bot',
          content: response.response,
          confidence: response.confidence,
          sources: response.sources,
          customFields: response.custom_fields
        });
      } else {
        // Always request debug info - user can toggle visibility per message
        const response = await sendChatMessage(message, projectId, true);
        addMessage({
          type: 'bot',
          content: response.response,
          confidence: response.confidence,
          sources: response.sources,
          debugInfo: response.debug_info
        });
      }
    } catch (error: any) {
      addMessage({
        type: 'error',
        content: `Error: ${error.response?.data?.detail || error.message || 'Failed to get response'}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Custom fields management
  const addCustomField = () => {
    setCustomFields(prev => [...prev, { name: '', description: '', field_type: 'text' }]);
  };

  const updateCustomField = (index: number, field: Partial<CustomField>) => {
    setCustomFields(prev => prev.map((f, i) => i === index ? { ...f, ...field } : f));
  };

  const removeCustomField = (index: number) => {
    setCustomFields(prev => prev.filter((_, i) => i !== index));
  };

  // CSV file handling
  const handleFileSelect = (file: File) => {
    if (file.name.endsWith('.csv')) {
      setSelectedFile(file);
    } else {
      alert('Please select a CSV file');
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) handleFileSelect(files[0]);
  };

  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const status: CSVJobStatus = await getCSVJobStatus(jobId);

      if (status.status === 'processing') {
        setMessages(prev => {
          const lastIdx = prev.length - 1;
          if (prev[lastIdx]?.type === 'system' && prev[lastIdx].content.includes('Processing')) {
            const updated = [...prev];
            const percent = status.total > 0 ? Math.round((status.progress / status.total) * 100) : 0;
            updated[lastIdx] = {
              ...updated[lastIdx],
              content: `📊 Processing CSV: ${status.progress}/${status.total} (${percent}%) - ${status.current_query || '...'}`
            };
            return updated;
          }
          return prev;
        });
        setTimeout(() => pollJobStatus(jobId), 2000);
      } else if (status.status === 'completed') {
        addMessage({
          type: 'system',
          content: `✅ CSV batch processing complete! ${status.total} queries processed.`,
          downloadJobId: jobId
        });
        setIsProcessingCSV(false);
      } else if (status.status === 'failed') {
        addMessage({ type: 'error', content: `❌ CSV processing failed: ${status.error}` });
        setIsProcessingCSV(false);
      } else if (status.status === 'pending') {
        setTimeout(() => pollJobStatus(jobId), 1000);
      }
    } catch (error: any) {
      addMessage({ type: 'error', content: `Error checking job status: ${error.message}` });
      setIsProcessingCSV(false);
    }
  }, [addMessage]);

  const handleCSVProcess = async () => {
    if (!selectedFile) return;
    setIsProcessingCSV(true);
    setShowCSVModal(false);

    addMessage({ type: 'system', content: `📤 Uploading CSV: ${selectedFile.name}...` });

    try {
      const validFields = customFields.filter(f => f.name && f.description);
      const result = await uploadCSVBatch(selectedFile, projectId, validFields);

      addMessage({ type: 'system', content: `📊 Processing CSV: Starting...` });
      pollJobStatus(result.job_id);
    } catch (error: any) {
      addMessage({ type: 'error', content: `❌ Upload failed: ${error.response?.data?.detail || error.message}` });
      setIsProcessingCSV(false);
    }
    setSelectedFile(null);
  };

  const handleDownload = (jobId: string) => {
    const url = getCSVDownloadUrl(jobId);
    const link = document.createElement('a');
    link.href = url;
    link.download = `compliance_responses_${jobId.substring(0, 8)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleDebug = (msgId: string) => {
    setExpandedDebug(prev => {
      const next = new Set(prev);
      if (next.has(msgId)) {
        next.delete(msgId);
      } else {
        next.add(msgId);
      }
      return next;
    });
  };

  const renderDebugPanel = (debugInfo: DebugInfo, msgId: string) => {
    const isExpanded = expandedDebug.has(msgId);

    return (
      <div className="debug-section">
        <button
          className={`debug-toggle-btn ${isExpanded ? 'expanded' : ''}`}
          onClick={() => toggleDebug(msgId)}
        >
          🔍 Debug {isExpanded ? '▼' : '▶'}
        </button>

        {isExpanded && (
          <div className="debug-panel">
            <div className="debug-header">
              <span className="debug-badge">
                Q&A Threshold: {Math.round(debugInfo.threshold_used * 100)}%
              </span>
              <span className={`debug-badge ${debugInfo.qa_match_used ? 'qa-used' : 'rag-used'}`}>
                {debugInfo.qa_match_used ? '📋 Q&A Match Used' : '📄 RAG Generated'}
              </span>
            </div>

            {debugInfo.qa_matches.length > 0 && (
              <div className="debug-subsection">
                <h5>📋 Q&A Matches (Top {debugInfo.qa_matches.length})</h5>
                <div className="debug-items">
                  {debugInfo.qa_matches.map((qa, idx) => (
                    <div key={idx} className={`debug-item ${idx === 0 && debugInfo.qa_match_used ? 'used' : ''}`}>
                      <div className="debug-item-header">
                        <span className={`similarity-score ${qa.similarity_score >= debugInfo.threshold_used ? 'above' : 'below'}`}>
                          {Math.round(qa.similarity_score * 100)}%
                        </span>
                        <span className="debug-meta">
                          {qa.client_name} • {qa.response_date}
                        </span>
                        {idx === 0 && debugInfo.qa_match_used && (
                          <span className="used-badge">✓ Used</span>
                        )}
                      </div>
                      <div className="debug-question">{qa.matched_question}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {debugInfo.policy_chunks.length > 0 && (
              <div className="debug-subsection">
                <h5>📄 Policy Document Chunks (Top {debugInfo.policy_chunks.length})</h5>
                <div className="debug-items">
                  {debugInfo.policy_chunks.map((chunk, idx) => (
                    <div key={idx} className="debug-item">
                      <div className="debug-item-header">
                        <span className="similarity-score above">
                          {Math.round(chunk.similarity_score * 100)}%
                        </span>
                        <span className="debug-doc-name">
                          {chunk.url ? (
                            <a href={chunk.url} target="_blank" rel="noopener noreferrer">
                              {chunk.doc_name}
                            </a>
                          ) : chunk.doc_name}
                        </span>
                      </div>
                      <div className="debug-preview">{chunk.text_preview}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {debugInfo.llm_prompt && (
              <div className="debug-subsection">
                <h5>🤖 LLM Prompt Sent to Gemini</h5>
                <div className="debug-prompt-container">
                  <button
                    className="copy-prompt-btn"
                    onClick={() => {
                      navigator.clipboard.writeText(debugInfo.llm_prompt || '');
                      alert('Prompt copied to clipboard!');
                    }}
                  >
                    📋 Copy Full Prompt
                  </button>
                  <pre className="debug-prompt">{debugInfo.llm_prompt}</pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderMessage = (msg: ChatMessage) => {
    const confidenceLevel = msg.confidence !== undefined
      ? msg.confidence >= 0.8 ? 'high' : msg.confidence >= 0.5 ? 'medium' : 'low'
      : null;

    // For bot messages, render markdown; for others, render plain text
    const renderContent = () => {
      if (msg.type === 'bot') {
        return (
          <div className="message-content markdown-content">
            <ReactMarkdown>{msg.content}</ReactMarkdown>
          </div>
        );
      }
      return <div className="message-content">{msg.content}</div>;
    };

    return (
      <div key={msg.id} className={`chat-message ${msg.type}`}>
        {renderContent()}

        <div className="message-actions">
          {confidenceLevel && (
            <span className={`confidence-badge ${confidenceLevel}`}>
              Confidence: {Math.round((msg.confidence || 0) * 100)}%
            </span>
          )}

          {msg.type === 'bot' && (
            <button
              className={`copy-response-btn ${copiedMsgId === msg.id ? 'copied' : ''}`}
              onClick={() => handleCopyResponse(msg)}
              title="Copy response with sources"
            >
              {copiedMsgId === msg.id ? '✓ Copied!' : '📋 Copy'}
            </button>
          )}
        </div>

        {msg.customFields && Object.keys(msg.customFields).length > 0 && (
          <div className="custom-fields-display">
            <h4>📋 Custom Fields</h4>
            {Object.entries(msg.customFields).map(([key, value]) => (
              <div key={key} className="custom-field-item">
                <span className="field-name">{key}:</span>
                <span className="field-value">
                  {Array.isArray(value)
                    ? value.join(', ')
                    : typeof value === 'string'
                      ? <ReactMarkdown>{value}</ReactMarkdown>
                      : String(value)}
                </span>
              </div>
            ))}
          </div>
        )}

        {msg.sources && msg.sources.length > 0 && (
          <div className="sources-display">
            <strong>Sources:</strong>
            <ul className="sources-list">
              {msg.sources.map((s, idx) => {
                // Handle different source formats
                if (typeof s === 'string') {
                  return <li key={idx}>{s}</li>;
                }
                if ('doc_name' in s) {
                  const docSource = s as { doc_name: string; url?: string };
                  if (docSource.url) {
                    return (
                      <li key={idx}>
                        <a href={docSource.url} target="_blank" rel="noopener noreferrer">
                          {docSource.doc_name}
                        </a>
                      </li>
                    );
                  }
                  return <li key={idx}>{docSource.doc_name}</li>;
                }
                if ('type' in s && s.type === 'previous_qa') {
                  const qaSource = s as { type: string; client: string };
                  return <li key={idx}>Previous Q&A ({qaSource.client})</li>;
                }
                return <li key={idx}>{JSON.stringify(s)}</li>;
              })}
            </ul>
          </div>
        )}

        {msg.downloadJobId && (
          <button className="download-btn" onClick={() => handleDownload(msg.downloadJobId!)}>
            📥 Download Results CSV
          </button>
        )}

        {msg.debugInfo && msg.type === 'bot' && renderDebugPanel(msg.debugInfo, msg.id)}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="chatbot-modal-overlay" onClick={onClose}>
      <div className="chatbot-modal" onClick={e => e.stopPropagation()}>
        <div className="chatbot-header">
          <div className="chatbot-title">
            <span className="chatbot-icon">🤖</span>
            <h2>Compliance Bot</h2>
            {projectName && <span className="project-badge">{projectName}</span>}
          </div>
          <button className="chatbot-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="chatbot-container" ref={chatContainerRef}>
          {messages.map(renderMessage)}
          {isLoading && (
            <div className="chat-message bot typing">
              <span></span><span></span><span></span>
            </div>
          )}
        </div>

        {showFieldsPanel && (
          <div className="fields-panel">
            <div className="fields-header">
              <h3>📋 Custom Response Fields</h3>
              <button className="add-field-btn" onClick={addCustomField}>+ Add Field</button>
            </div>
            <div className="fields-list">
              {customFields.map((field, idx) => (
                <div key={idx} className="field-row">
                  <input
                    type="text"
                    placeholder="Field name"
                    value={field.name}
                    onChange={e => updateCustomField(idx, { name: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="What should this field contain?"
                    value={field.description}
                    onChange={e => updateCustomField(idx, { description: e.target.value })}
                    style={{ flex: 2 }}
                  />
                  <select
                    value={field.field_type}
                    onChange={e => updateCustomField(idx, { field_type: e.target.value as any })}
                  >
                    <option value="text">Text</option>
                    <option value="yes_no">Yes/No</option>
                    <option value="list">List</option>
                    <option value="number">Number</option>
                  </select>
                  <button className="remove-field-btn" onClick={() => removeCustomField(idx)}>✕</button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="chatbot-input-area">
          <button
            className={`toggle-fields-btn ${showFieldsPanel ? 'active' : ''}`}
            onClick={() => setShowFieldsPanel(!showFieldsPanel)}
          >
            📋 Fields {customFields.length > 0 && `(${customFields.length})`}
          </button>
          <button
            className="csv-upload-btn"
            onClick={() => setShowCSVModal(true)}
            disabled={isProcessingCSV}
          >
            📤 CSV Batch
          </button>
          <input
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask a compliance question..."
            disabled={isLoading}
          />
          <button
            className="send-btn"
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
          >
            Send
          </button>
        </div>

        {/* CSV Upload Modal */}
        {showCSVModal && (
          <div className="csv-modal-overlay" onClick={() => setShowCSVModal(false)}>
            <div className="csv-modal" onClick={e => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowCSVModal(false)}>✕</button>
              <h2>📤 CSV Batch Processing</h2>
              <div className="csv-info">
                <strong>CSV Format:</strong> Column A must have header "Query" with questions.
                Any additional columns will be preserved in the output.
              </div>
              <div
                className={`file-drop ${selectedFile ? 'has-file' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={handleFileDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={e => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                <p>📁 Click or drag CSV file here</p>
                {selectedFile && <p className="selected-file">{selectedFile.name}</p>}
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowCSVModal(false)}>Cancel</button>
                <button
                  className="btn-primary"
                  onClick={handleCSVProcess}
                  disabled={!selectedFile}
                >
                  Process CSV
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComplianceChatbot;

